import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import LoadingCard from "components/LoadingCard/LoadingCard";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import RedirectForm from "./RedirectForm";
import {
  createRedirect,
  deleteRedirect,
  errorMessage,
  fetchRedirects,
  updateRedirect,
} from "./redirectsApi";

const { Title, Text } = Typography;

/**
 * URL redirects for the marketing site.
 *
 * The list is a build-time input: hpn-landing reads it and emits the host's
 * redirect rules, so a change here takes effect at the next site build, not at
 * save time. The screen says so rather than letting an editor test a rule
 * immediately and conclude it did not work.
 *
 * A `List` rather than a `Table` on purpose — the rows are two long paths, and
 * a table of long strings is unreadable on the phone this admin is mostly used
 * from. Filtering client-side is fine at the size this collection stays: it is
 * the retired WordPress URLs, which is a fixed set that only shrinks.
 */
export default function Redirects() {
  const [redirects, setRedirects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // Already sorted by source path — the API orders the list.
      setRedirects(await fetchRedirects());
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isNew = editing != null && !editing._id;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return redirects;
    return redirects.filter(
      (redirect) =>
        redirect.from?.toLowerCase().includes(needle) ||
        redirect.to?.toLowerCase().includes(needle)
    );
  }, [redirects, search]);

  const openEditor = (redirect) => {
    setEditing(redirect ?? {});
    form.setFieldsValue({
      from: redirect?.from ?? "",
      to: redirect?.to ?? "",
      // New rules default to permanent, matching the API's own default.
      permanent: redirect ? redirect.permanent !== false : true,
      note: redirect?.note ?? "",
    });
  };

  const closeEditor = () => {
    setEditing(null);
    form.resetFields();
  };

  const submit = async (values) => {
    setIsSaving(true);
    try {
      const payload = {
        from: values.from.trim(),
        to: values.to.trim(),
        permanent: Boolean(values.permanent),
        note: values.note || "",
      };

      if (isNew) await createRedirect(payload);
      else await updateRedirect(editing._id, payload);

      message.success("Запазено — сайтът ще го приложи при следващото изграждане");
      closeEditor();
      await load();
    } catch (error) {
      // A 409 from the unique constraint arrives here naming the path, which is
      // the only message worth showing: it means the rule already exists.
      message.error(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (redirect) => {
    try {
      await deleteRedirect(redirect._id);
      message.success("Изтрито");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Пренасочвания
        </Title>
        <Text type="secondary">
          Пренасочваш стар адрес към нов. Промените стигат до сайта при следващото му
          изграждане.
        </Text>
      </Space>

      <Button
        type="primary"
        block
        style={{ ...CONTROL, marginBottom: 16 }}
        onClick={() => openEditor(null)}
      >
        Ново пренасочване
      </Button>

      {isLoading && <LoadingCard />}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на пренасочванията"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <>
          <Input.Search
            allowClear
            placeholder="Филтрирай по адрес"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ ...CONTROL, marginBottom: 16 }}
          />

          <List
            bordered
            dataSource={visible}
            locale={{
              emptyText: search ? "Няма пренасочване, което да съвпада." : "Все още няма пренасочвания.",
            }}
            renderItem={(redirect) => (
              <List.Item style={{ display: "block", padding: 16 }}>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Space wrap size={8}>
                    <Tag color={redirect.permanent === false ? "orange" : "blue"}>
                      {redirect.permanent === false ? "302 временно" : "301 постоянно"}
                    </Tag>
                  </Space>

                  {/* Paths break anywhere: a long one must wrap, not widen the page. */}
                  <Text strong style={{ wordBreak: "break-all" }}>
                    {redirect.from}
                  </Text>
                  <Text type="secondary" style={{ wordBreak: "break-all" }}>
                    → {redirect.to}
                  </Text>
                  {redirect.note && <Text type="secondary">{redirect.note}</Text>}

                  <Space wrap size={8}>
                    <Button style={CONTROL} onClick={() => openEditor(redirect)}>
                      Редактирай
                    </Button>
                    <Popconfirm
                      title="Сигурен ли си, че искаш да изтриеш това пренасочване?"
                      description="Посетителите на стария адрес отново ще виждат 404."
                      okText="Изтрий"
                      cancelText="Отказ"
                      onConfirm={() => remove(redirect)}
                    >
                      <Button danger style={CONTROL}>
                        Изтрий
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </List.Item>
            )}
          />
        </>
      )}

      <Modal
        open={editing != null}
        title={isNew ? "Ново пренасочване" : "Редактиране на пренасочване"}
        onCancel={closeEditor}
        onOk={form.submit}
        confirmLoading={isSaving}
        okText="Запази"
        cancelText="Отказ"
        destroyOnClose
      >
        <RedirectForm form={form} onSubmit={submit} />
      </Modal>
    </div>
  );
}
